import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import { Button, Grid, Stack, Text, useTheme } from '@ltvco/refresh-lib/theme';
import { ReportSection } from '@ltvco/refresh-lib/v2';
import { BusinessTwoTone, SvgIconComponent } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { theme } from 'theme';

import { PROFESSIONAL_TOOL_CARDS } from './PropertyReportProfessionalTools.constants';
import {
  ProfessionalToolCardRoot,
  ProfessionalToolIconCircle,
} from './PropertyReportProfessionalTools.styles';

type ProfessionalToolLinkCardProps = {
  title: string;
  description: string;
  Icon: SvgIconComponent;
  href: string;
  ctaLabel: string;
  tracking?: {
    type: string;
  };
};

function ProfessionalToolLinkCard({
  title,
  description,
  Icon,
  href,
  ctaLabel,
  tracking,
}: ProfessionalToolLinkCardProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { trackEventGA4 } = useAppConfigContext();

  const handleClick = () => {
    if (tracking) {
      trackEventGA4({
        event_name: 'report_element_engagement',
        type: tracking.type,
        interacted_from: 'property_report_business_row',
      });
    }
    navigate(href);
  };

  return (
    <ProfessionalToolCardRoot>
      <ProfessionalToolIconCircle>
        {<Icon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />}
      </ProfessionalToolIconCircle>
      <Stack gap={1}>
        <Text
          variant="body1"
          fontWeight="bold"
          mb={0.5}
          sx={{ color: theme.palette.text.primary }}
        >
          {title}
        </Text>
        <Text variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {description}
        </Text>
        <Button
          variant="text"
          onClick={handleClick}
          sx={{ alignSelf: 'flex-start' }}
        >
          <Text variant="body2" fontWeight="bold">
            {ctaLabel}
          </Text>
        </Button>
      </Stack>
    </ProfessionalToolCardRoot>
  );
}

export function PropertyReportProfessionalTools() {
  return (
    <ReportSection
      sectionId="professional-tools"
      title="Professional Tools"
      titleIcon={<BusinessTwoTone sx={{ mr: 0.5 }} />}
      sx={{ marginTop: theme.spacing(2.5) }}
      hasContent
    >
      <Grid container spacing={2} sx={{ mt: 4, mb: 2 }}>
        {PROFESSIONAL_TOOL_CARDS.map(
          ({ id, title, description, Icon, href, ctaLabel, tracking }) => (
            <Grid key={id} size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
              <ProfessionalToolLinkCard
                title={title}
                description={description}
                Icon={Icon}
                href={href}
                ctaLabel={ctaLabel}
                tracking={tracking}
              />
            </Grid>
          )
        )}
      </Grid>
    </ReportSection>
  );
}
